import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { SITE } from "@/lib/site";
import { hreflangLinks } from "@/lib/hreflang";
import solAuditorium from "@/assets/sol-auditorium.jpg";
import solEnterprise from "@/assets/sol-enterprise.jpg";
import solCinema from "@/assets/sol-cinema.jpg";
import {
  pageSchemaScript,
  PERSON_ID,
  PORTRAIT_ID,
  SITE_URL,
  PRIMARY_PORTRAIT_PATH,
  PRIMARY_PORTRAIT_WIDTH,
  PRIMARY_PORTRAIT_HEIGHT,
} from "@/seo/schema";

const PATH = "/av-consultant-in-india";
const URL = `${SITE_URL}${PATH}`;
const TITLE = "AV Consultant in India | Audio Visual Solutions | Arif Khan";
const DESC =
  "Arif Khan plans audio-visual systems in India for auditoriums, corporate boardrooms and video conferencing, and luxury homes with cinemas and multi-room audio.";

const FAQS = [
  { q: "What does an AV consultant do?", a: "An AV consultant works out what a room needs people to hear and see, then plans the audio, video, conferencing and control systems to deliver it, and coordinates them with the architecture, interiors, lighting and network before equipment is chosen." },
  { q: "When should an AV consultant be involved in a project?", a: "Before ceilings, furniture, joinery, electrical layouts and network infrastructure are frozen, so speaker, display, rack and cable positions can be built into the design." },
  { q: "What is included in an auditorium AV system?", a: "Typically loudspeakers, amplification and processing, microphones, mixing and stage monitoring, projection or LED display, presentation sources and switching, a control system, and where required recording or streaming, supported by racks, cabling, power and a control position." },
  { q: "How should AV be planned for a corporate boardroom?", a: "Start with how meetings happen: the number of people, local and remote participants and how content is shared. Then plan display size for viewing distance, microphone and camera coverage, clear speech, simple connectivity and one straightforward control interface." },
  { q: "What is conference-room automation?", a: "Conference-room automation links the display, conferencing system, sources, volume, microphones, lighting and blinds so a room can be set up with a few simple actions, such as a single mode for a video call, where the chosen control system supports it." },
  { q: "What should be considered when designing a luxury home cinema?", a: "Room dimensions, seating layout, screen size, projector or display, speaker and subwoofer positions, amplification and processing, room acoustics, sound isolation, equipment ventilation, lighting and control. Equipment price alone does not decide how a cinema performs." },
  { q: "How does multi-room audio work in a luxury home?", a: "The home is divided into zones, such as living areas, bedrooms, kitchen and terrace. Each zone can play its own source at its own volume, or share the same music, controlled from a keypad, touch panel or app, often linked to home automation." },
  { q: "How should outdoor audio be designed for a farmhouse or villa?", a: "By planning coverage for each area, speaker positions that suit the landscape, weather-exposure protection, zoning and cable routes, aiming for even sound at sensible listening levels rather than a few very loud speakers." },
  { q: "What is the difference between an AV consultant and an acoustic consultant?", a: "An acoustic consultant designs how the room itself behaves: sound isolation, reverberation and acoustic treatment. An AV consultant plans the audio, video and control systems used in the room. On projects that need both, the two work together." },
  { q: "Can AV systems be integrated with building or home automation?", a: "Yes, where the selected systems support it. AV can share control with lighting, blinds, climate and security interfaces, so users operate the space from one simple interface." },
];

export const Route = createFileRoute("/av-consultant-in-india")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(PATH)],
    scripts: [
      pageSchemaScript(
        {
          path: PATH,
          type: "WebPage",
          name: TITLE,
          description: DESC,
          breadcrumb: [{ name: "AV Consultant in India" }],
          extra: {
            about: [{ "@id": PERSON_ID }, { "@id": `${URL}#service` }],
            mainEntity: { "@id": PERSON_ID },
            primaryImageOfPage: { "@id": PORTRAIT_ID },
          },
        },
        [
          {
            "@type": "Service",
            "@id": `${URL}#service`,
            name: "Audio-visual planning and integration",
            serviceType: "AV consulting",
            provider: { "@id": PERSON_ID },
            areaServed: { "@type": "Country", name: "India" },
            url: URL,
          },
          {
            "@type": "FAQPage",
            "@id": `${URL}#faq`,
            isPartOf: { "@id": `${URL}#webpage` },
            mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
          },
        ],
      ),
    ],
  }),
});

const PROOF = [
  { value: "23+ Years", label: "Building technology, security, AV & automation experience" },
  { value: "1,000+ Projects", label: "Across all disciplines" },
  { value: "500 km+", label: "Perimeter security experience (not AV)" },
];

const UNDERSTAND = ["What the room is meant to do", "Who will use it, and how many people", "What they need to hear and see", "How they will present, meet, watch or listen", "How AV meets the architecture, interiors, lighting, network and control"];

const AUDIENCES = [
  { t: "Auditoriums & institutional AV", img: solAuditorium, alt: "Auditorium with stage, seating and audio-visual systems", who: "Universities, schools, institutions, corporate auditoriums and multipurpose halls.", items: ["Speech reinforcement", "Music reproduction", "Microphones & digital mixing", "Stage monitoring", "Projection or LED display", "Presentation systems", "Stage lighting interfaces (where in scope)", "Motorised stage curtains (where in scope)", "Control systems", "Recording / streaming where required", "Commissioning"] },
  { t: "Corporate AV & collaboration", img: solEnterprise, alt: "Corporate boardroom with display and conferencing equipment", who: "Boardrooms, conference and executive meeting rooms, training rooms and town halls.", items: ["Video conferencing", "Wireless presentation", "Microphones & speakers", "Displays / projection", "Conferencing cameras", "AV-over-IP where relevant", "Integrated room control", "Lighting & curtain interfaces"] },
  { t: "Luxury residential AV", img: solCinema, alt: "Private home cinema in a luxury residence", who: "UHNI residences, villas and farmhouses.", items: ["Luxury home cinema", "Media rooms", "Multi-room audio", "Outdoor audio", "Living-room AV & display integration", "Music streaming", "Concealed AV approaches", "Integrated control with home automation"] },
];

const AUD_PARTS = [
  { t: "Audio", b: "Speakers, amplification, DSP, microphones, mixing and monitoring." },
  { t: "Video", b: "Projection or LED displays, switching, presentation, confidence displays and content sources where applicable." },
  { t: "Stage", b: "Stage inputs, stage monitors, microphone provisions, stage connectivity and AV interfaces." },
  { t: "Stage curtains", b: "Main curtains, masking or other motorised curtain requirements, where within supplied scope." },
  { t: "Control", b: "Integrated control of AV equipment and appropriate room functions." },
  { t: "Infrastructure", b: "Cabling, equipment racks, control room, power, UPS, network and pathways." },
  { t: "Coordination", b: "With architects, acoustics specialists, electrical/MEP, interiors, seating, lighting and stage contractors." },
];

const CORP = ["Display size matched to viewing distance", "Microphone coverage for every seat", "Clear speech reproduction", "Conferencing camera placement", "Conferencing platform requirements (e.g. Teams or Zoom) where applicable", "Content sharing, wired and wireless", "Room control", "Lighting and blinds interfaces", "Network requirements", "Equipment-rack location"];
const AUTOMATION = ["Display / projector", "Conferencing system", "Source selection", "Volume", "Microphones", "Lighting", "Motorised blinds or curtains", "Room modes / scenes"];
const CINEMA = ["Room dimensions", "Seating layout", "Screen size", "Projector / display", "Speaker configuration", "Subwoofer placement", "Amplification & processing", "Room acoustics", "Sound isolation", "Equipment ventilation", "Lighting", "Automation / control", "Source devices"];
const ROOMS = ["Bedrooms", "Living areas", "Kitchens", "Lounges", "Bathrooms where appropriate", "Terraces", "Gardens", "Entertainment areas"];
const OUTDOOR = ["Area coverage", "Speaker positioning", "Weather exposure", "Landscape aesthetics", "Zoning", "Even volume distribution", "Cabling"];
const EARLY = ["Speaker locations", "Screen / projector positions", "Projector throw", "Display mounting", "Cable routes", "Conduits", "Rack space", "Equipment ventilation", "Power", "Network", "Ceiling coordination", "Lighting", "Curtain / blind provisions", "Seating", "Touch-panel locations", "Floor boxes", "Stage boxes"];

const MISTAKES = [
  "Selecting products before understanding the room's requirements.",
  "Buying larger speakers rather than designing coverage.",
  "Choosing screen size without considering viewing distance.",
  "Ignoring room acoustics.",
  "Placing microphones without considering the seating layout.",
  "Treating networking as separate from modern AV.",
  "Insufficient rack space or cooling.",
  "Poor cable pathways.",
  "Bringing AV in after ceilings and interiors are finalised.",
  "Conference rooms that need several remotes and complicated steps.",
  "Luxury homes with expensive equipment but poor integration.",
  "Outdoor speakers positioned for volume instead of even coverage.",
];

const STAGES = [
  { n: "01", t: "Understand", b: "Room purpose, users, architecture, listening and viewing needs, and how the space will be operated." },
  { n: "02", t: "Design", b: "System architecture, equipment categories, locations, infrastructure and control requirements." },
  { n: "03", t: "Coordinate", b: "With architects, interior designers, acoustics specialists, MEP/electrical, IT/network teams and contractors." },
  { n: "04", t: "Integrate & verify", b: "Working with the Altima Global team through installation, programming, system set-up, testing, commissioning and handover as required." },
];

const PRIORITIES = [
  { t: "Auditorium", b: "Speech and music coverage, stage requirements, display, control and large-audience operation." },
  { t: "Corporate boardroom", b: "Speech intelligibility, conferencing, camera and microphone coverage, presentation and simplicity." },
  { t: "Luxury home cinema", b: "Immersion, room integration, viewing, listening, acoustics and discreet control." },
  { t: "Multi-room residence", b: "Ease of use, zoning, aesthetics and integration." },
  { t: "Outdoor AV", b: "Coverage, weather exposure, landscape coordination and zoning." },
];

const COORD = ["Ceiling coordination", "Speaker placement", "Projector / display positioning", "Equipment concealment", "Rack locations", "Ventilation", "Conduits", "Power", "Network", "Stage connections", "Control panels", "Lighting interfaces", "Curtain / blind provisions"];

function Section({ id, title, children, alt }: { id: string; title: string; children: React.ReactNode; alt?: boolean }) {
  return (
    <section aria-labelledby={id} className={`py-20 md:py-24 ${alt ? "border-y border-border bg-surface" : ""}`}>
      <div className="container-page">
        <h2 id={id} className="text-3xl md:text-4xl font-semibold tracking-tight max-w-3xl">{title}</h2>
        <div className="mt-8">{children}</div>
      </div>
    </section>
  );
}

function Chips({ items, bg }: { items: string[]; bg?: boolean }) {
  return (
    <ul className="flex flex-wrap gap-2 max-w-5xl">
      {items.map((i) => <li key={i} className={`rounded-full border border-border px-4 py-2 text-sm ${bg ? "bg-background" : ""}`}>{i}</li>)}
    </ul>
  );
}

const linkCls = "mt-6 inline-flex items-center gap-1 text-sm font-medium text-primary";

function Page() {
  return (
    <PageShell>
      <section className="relative pt-40 pb-20 border-b border-border bg-surface">
        <div className="container-page grid gap-12 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7">
            <span className="eyebrow">Audio Visual · India</span>
            <h1 className="mt-5 text-balance text-5xl md:text-6xl font-semibold tracking-tight leading-[0.95]">
              Audio Visual (AV) Consultant in India
            </h1>
            <p className="mt-6 text-lg text-ink-soft leading-relaxed max-w-2xl">
              Arif Khan has 23+ years of experience across building technology, electronic security, audio-video, automation and integrated systems. He works with project owners, architects, consultants and design teams on AV for corporate, institutional and luxury residential spaces, from assessing requirements and shaping the AV concept and system architecture to technology selection, design coordination, and working with the integration team through testing and commissioning.
            </p>
            <p className="mt-3 text-sm text-ink-soft">BU Head, Security &amp; Automation, Altima Global · Projects across India</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="tap-target inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground">
                Discuss an AV Project <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
              <Link to="/solutions" className="tap-target inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium">
                View AV &amp; Cinema Solutions
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img
              src={PRIMARY_PORTRAIT_PATH}
              alt="Arif Khan, audio-visual and building technology consultant, BU Head, Security & Automation at Altima Global"
              width={PRIMARY_PORTRAIT_WIDTH}
              height={PRIMARY_PORTRAIT_HEIGHT}
              loading="eager"
              decoding="async"
              className="w-full aspect-[4/5] object-cover object-[62%_30%] rounded-3xl border border-border"
            />
          </div>
        </div>
      </section>

      <Section id="credibility" title="Experience at a glance">
        <dl className="grid gap-6 sm:grid-cols-3">
          {PROOF.map((s) => (
            <div key={s.value} className="rounded-2xl border border-border p-6">
              <dd className="text-4xl font-semibold tracking-tight">{s.value}</dd>
              <dt className="mt-2 text-sm text-ink-soft">{s.label}</dt>
            </div>
          ))}
        </dl>
      </Section>

      <Section id="what" title="What does an AV consultant do?" alt>
        <p className="max-w-3xl text-lg text-ink-soft">A good AV consultant starts with the room and the people in it, not the equipment. The first questions are:</p>
        <ul className="mt-6 grid gap-3 max-w-3xl list-disc pl-5 text-ink-soft">{UNDERSTAND.map((u) => <li key={u}>{u}</li>)}</ul>
        <p className="mt-6 max-w-3xl text-lg text-ink-soft">Only once those answers are clear does equipment selection follow.</p>
      </Section>

      <Section id="audiences" title="One AV discipline, three very different environments">
        <div className="grid gap-6 lg:grid-cols-3">
          {AUDIENCES.map((a) => (
            <article key={a.t} className="overflow-hidden rounded-2xl border border-border">
              <img src={a.img} alt={a.alt} loading="lazy" decoding="async" width={800} height={500} className="w-full aspect-[16/10] object-cover" />
              <div className="p-6">
                <h3 className="text-lg font-semibold">{a.t}</h3>
                <p className="mt-2 text-ink-soft">{a.who}</p>
                <ul className="mt-4 list-disc pl-5 text-sm text-ink-soft space-y-1">{a.items.map((i) => <li key={i}>{i}</li>)}</ul>
              </div>
            </article>
          ))}
        </div>
        <p className="mt-6 max-w-3xl text-ink-soft">Auditorium AV is planned together with room acoustics, seating, sightlines, stage requirements, architecture and electrical infrastructure. Where a specialist acoustic consultant is appointed, Arif Khan and Altima Global coordinate the AV with the acoustic and architectural team.</p>
      </Section>

      <Section id="auditorium" title="Auditorium audio visual solutions" alt>
        <p className="max-w-3xl text-lg text-ink-soft">A successful auditorium is more than loudspeakers and a large screen. Several parts have to be planned as one system:</p>
        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {AUD_PARTS.map((p) => (
            <div key={p.t} className="rounded-2xl border border-border bg-background p-6">
              <h3 className="text-lg font-semibold">{p.t}</h3>
              <p className="mt-3 text-ink-soft">{p.b}</p>
            </div>
          ))}
        </div>
        <Link to="/solutions/$slug" params={{ slug: "auditorium-av" }} className={linkCls}>Auditorium AV solutions <ArrowRight className="h-4 w-4" aria-hidden /></Link>
      </Section>

      <Section id="corporate" title="Corporate AV, video conferencing & meeting rooms">
        <p className="max-w-3xl text-lg text-ink-soft">A meeting room should work without needing an AV engineer every time someone wants to join a call. That comes from planning:</p>
        <div className="mt-6"><Chips items={CORP} /></div>
        <Link to="/solutions/$slug" params={{ slug: "enterprise-av" }} className={linkCls}>Enterprise AV solutions <ArrowRight className="h-4 w-4" aria-hidden /></Link>
      </Section>

      <Section id="room-automation" title="Conference room automation" alt>
        <p className="max-w-3xl text-lg text-ink-soft">Conference-room automation should make a room simpler to use, not add more control panels. Functions that can be brought under one interface include:</p>
        <div className="mt-6"><Chips items={AUTOMATION} bg /></div>
        <p className="mt-6 max-w-3xl text-ink-soft">Where the selected control system supports it, one-touch modes such as Presentation, Video Call or Board Meeting can set up the room in a single step.</p>
        <Link to="/elv-consultant-in-india" className={linkCls}>ELV &amp; Building Technology Consultant in India <ArrowRight className="h-4 w-4" aria-hidden /></Link>
      </Section>

      <Section id="cinema" title="Luxury home cinema">
        <p className="max-w-3xl text-lg text-ink-soft">A serious home cinema comes from coordinated decisions about the room, not from the price of the equipment. The decisions that matter:</p>
        <div className="mt-6"><Chips items={CINEMA} /></div>
        <p className="mt-6 max-w-3xl text-ink-soft">Equipment price alone does not decide how a cinema looks and sounds; the room, layout and integration do.</p>
        <Link to="/solutions/$slug" params={{ slug: "luxury-home-cinema" }} className={linkCls}>Luxury Home Cinema <ArrowRight className="h-4 w-4" aria-hidden /></Link>
      </Section>

      <Section id="multiroom" title="Multi-room audio" alt>
        <p className="max-w-3xl text-lg text-ink-soft">One home can have many listening experiences without visible equipment in every room. The home is divided into zones; each can play its own music at its own volume, or share the same source, controlled from a keypad, touch panel or app and linked to home automation.</p>
        <div className="mt-6"><Chips items={ROOMS} bg /></div>
        <Link to="/solutions/$slug" params={{ slug: "smart-automation" }} className={linkCls}>Smart Automation <ArrowRight className="h-4 w-4" aria-hidden /></Link>
      </Section>

      <Section id="outdoor" title="Outdoor audio">
        <p className="max-w-3xl text-lg text-ink-soft">Farmhouses, lawns, gardens, pool areas, terraces and entertainment zones need their own audio plan. The aim is even coverage at sensible listening levels, not a few very powerful speakers. Planning covers:</p>
        <div className="mt-6"><Chips items={OUTDOOR} /></div>
      </Section>

      <Section id="early" title="AV design before interiors are finalised" alt>
        <p className="max-w-3xl text-lg text-ink-soft">AV works best when it is coordinated with the architect and interior designer early. Late decisions lead to visible cabling, compromised speaker positions, rework or equipment forced into unsuitable places. Items to settle early:</p>
        <div className="mt-6"><Chips items={EARLY} bg /></div>
      </Section>

      <Section id="mistakes" title="Common AV planning mistakes">
        <ul className="grid gap-3 md:grid-cols-2 max-w-5xl">
          {MISTAKES.map((m) => <li key={m} className="rounded-xl border border-border p-4 text-ink-soft">{m}</li>)}
        </ul>
      </Section>

      <Section id="method" title="Arif Khan's AV project approach" alt>
        <ol className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {STAGES.map((s) => (
            <li key={s.n} className="rounded-2xl border border-border bg-background p-6">
              <span className="eyebrow">{s.n}</span>
              <h3 className="mt-2 text-lg font-semibold">{s.t}</h3>
              <p className="mt-3 text-ink-soft">{s.b}</p>
            </li>
          ))}
        </ol>
      </Section>

      <Section id="roles" title="Consulting, design & execution">
        <p className="max-w-3xl text-lg text-ink-soft">The process starts with the room, user and functional requirement rather than a preferred product or brand. Equipment is selected after the performance, architectural and integration requirements are understood.</p>
        <div className="mt-8 grid gap-6 md:grid-cols-2 max-w-5xl">
          <div className="rounded-2xl border border-border p-6">
            <h3 className="text-lg font-semibold">Planning &amp; Design Coordination</h3>
            <p className="mt-3 text-ink-soft">Requirements, architecture, system concept, equipment selection, infrastructure and coordination.</p>
          </div>
          <div className="rounded-2xl border border-border p-6">
            <h3 className="text-lg font-semibold">Integration &amp; Execution</h3>
            <p className="mt-3 text-ink-soft">Supply, installation, programming, integration, testing, commissioning and handover.</p>
          </div>
        </div>
        <p className="mt-6 max-w-3xl text-ink-soft">Arif Khan leads the AV planning and solution approach and works with the Altima Global integration team through implementation, testing and commissioning. His involvement may include consulting, design coordination, technology selection and implementation oversight depending on the project.</p>
      </Section>

      <Section id="priorities" title="How different AV projects need different design priorities" alt>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-5">
          {PRIORITIES.map((p) => (
            <div key={p.t} className="rounded-2xl border border-border bg-background p-6">
              <h3 className="text-lg font-semibold">{p.t}</h3>
              <p className="mt-3 text-sm text-ink-soft">{p.b}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 max-w-3xl font-medium">AV is one discipline, but the design priorities change significantly with the space and the people using it.</p>
      </Section>

      <Section id="architects" title="For architects, interior designers & consultants">
        <p className="max-w-3xl text-lg text-ink-soft">Involving AV planning before ceilings, furniture, joinery and electrical layouts are frozen keeps equipment where it performs best and out of sight where it should be. Coordination points:</p>
        <div className="mt-6"><Chips items={COORD} /></div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/contact" className="tap-target inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground">Discuss AV Design Coordination <ArrowRight className="h-4 w-4" aria-hidden /></Link>
          <Link to="/for-architects" className="tap-target inline-flex items-center rounded-full border border-border px-6 py-3 text-sm font-medium">For Architects</Link>
        </div>
      </Section>

      <Section id="work" title="Selected AV experience" alt>
        {/* [PROOF NEEDED] International Centre for Performing Arts & Culture, Shillong (auditorium AV): omitted until name, scope, systems, location and permission to reference are confirmed. */}
        {/* [PROOF NEEDED] Any named corporate AV, boardroom or home cinema project: none published until verified. */}
        <div className="grid gap-6 md:grid-cols-2 max-w-4xl">
          <div className="rounded-2xl border border-border bg-background p-6">
            <span className="eyebrow">Luxury Residences · Delhi NCR</span>
            <h3 className="mt-2 text-lg font-semibold">Residences &amp; farmhouses for HNI clients</h3>
            <p className="mt-3 text-ink-soft">Multi-year engagements covering estate security and automation, planned before interiors are frozen.</p>
          </div>
          <div className="rounded-2xl border border-border bg-background p-6">
            <span className="eyebrow">More work</span>
            <h3 className="mt-2 text-lg font-semibold">Projects portfolio</h3>
            <p className="mt-3 text-ink-soft">See engagements across security, automation and AV.</p>
            <Link to="/projects" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">View projects <ArrowRight className="h-4 w-4" aria-hidden /></Link>
          </div>
        </div>
      </Section>

      <Section id="faq" title="Frequently asked questions">
        <div className="max-w-3xl divide-y divide-border border-y border-border">
          {FAQS.map((f) => (
            <div key={f.q} className="py-6">
              <h3 className="text-lg font-semibold">{f.q}</h3>
              <p className="mt-2 text-ink-soft">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="related" title="Related expertise" alt>
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl">
          <li><Link to="/elv-consultant-in-india" className="text-primary">ELV &amp; Building Technology Consultant in India</Link></li>
          <li><Link to="/security-consultant-in-india" className="text-primary">Security Consultant in India</Link></li>
          <li><Link to="/solutions/$slug" params={{ slug: "auditorium-av" }} className="text-primary">Auditorium AV</Link></li>
          <li><Link to="/solutions/$slug" params={{ slug: "enterprise-av" }} className="text-primary">Enterprise AV</Link></li>
          <li><Link to="/solutions/$slug" params={{ slug: "luxury-home-cinema" }} className="text-primary">Luxury Home Cinema</Link></li>
          <li><Link to="/solutions/$slug" params={{ slug: "smart-automation" }} className="text-primary">Smart Automation</Link></li>
          <li><Link to="/projects" className="text-primary">Projects</Link></li>
          <li><Link to="/for-architects" className="text-primary">For Architects</Link></li>
          <li><Link to="/contact" className="text-primary">Contact</Link></li>
          <li><Link to="/ask-arif/$slug" params={{ slug: "auditorium-av-planning-guide" }} className="text-primary">Auditorium AV planning guide</Link></li>
          <li><Link to="/ask-arif/$slug" params={{ slug: "what-makes-an-auditorium-av-system-work" }} className="text-primary">What makes an auditorium AV system work</Link></li>
          <li><Link to="/ask-arif/$slug" params={{ slug: "home-cinema-acoustic-design-basics" }} className="text-primary">Home cinema acoustic design basics</Link></li>
          <li><Link to="/ask-arif/$slug" params={{ slug: "common-mistakes-in-home-theatre-projects" }} className="text-primary">Common mistakes in home theatre projects</Link></li>
        </ul>
      </Section>

      <section aria-labelledby="cta" className="py-24 border-t border-border bg-surface">
        <div className="container-page text-center max-w-3xl">
          <h2 id="cta" className="text-3xl md:text-4xl font-semibold tracking-tight">Planning an AV system before the room is finished?</h2>
          <p className="mt-4 text-ink-soft">The strongest time to coordinate AV is before the architecture, interiors, electrical layouts and network infrastructure are frozen. Email {SITE.email} or call {SITE.phone}.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="tap-target inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground">Discuss an AV Project <ArrowRight className="h-4 w-4" aria-hidden /></Link>
            <Link to="/contact" className="tap-target inline-flex items-center rounded-full border border-border px-6 py-3 text-sm font-medium">Contact Arif Khan</Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
